import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Drawer } from "../Drawer";
import { Icon } from "../ui/Icon";
import { SearchSelect } from "../ui/SearchSelect";
import { ConfirmDialog } from "../ui/ConfirmDialog";
import { AttachmentPanel } from "./AttachmentPanel";
import { MaintenancePurchaseRequestDrawer } from "./MaintenancePurchaseRequestDrawer";
import { trpc } from "../../lib/trpc";
import { Link } from "react-router-dom";

const PO_STATUS_LABEL: Record<string, { label: string; cls: string }> = {
  draft: { label: "Taslak", cls: "b-warn" },
  confirmed: { label: "Onaylı", cls: "b-info" },
  partially_received: { label: "Kısmi Teslim", cls: "b-info" },
  received: { label: "Teslim Alındı", cls: "b-success" },
  cancelled: { label: "İptal", cls: "b-danger" },
};

type OrderStatus = "open" | "in_progress" | "waiting_parts" | "completed" | "cancelled";
type Severity = "low" | "medium" | "high" | "critical";

const STATUS_META: Record<OrderStatus, { label: string; cls: string; icon: string }> = {
  open: { label: "Açık", cls: "b-warn", icon: "clock" },
  in_progress: { label: "Devam Ediyor", cls: "b-info", icon: "play" },
  waiting_parts: { label: "Parça Bekliyor", cls: "b-warn", icon: "boxes" },
  completed: { label: "Tamamlandı", cls: "b-success", icon: "check" },
  cancelled: { label: "İptal", cls: "b-danger", icon: "x" },
};
const SEV_LABELS: Record<Severity, string> = { low: "Düşük", medium: "Orta", high: "Yüksek", critical: "Kritik" };
const TYPE_LABELS: Record<string, string> = { corrective: "Arıza / Onarım", preventive: "Periyodik Bakım" };

function fmtQty(n: number): string { return new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 3 }).format(n); }
function fmtMoney(n: number): string { return new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 2 }).format(n); }
function fmtDateTime(iso: string | null): string { return iso ? new Date(iso).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" }) : "—"; }
function fmtDate(iso: string | null): string { return iso ? new Date(iso).toLocaleDateString("tr-TR") : "—"; }

interface Props {
  open: boolean;
  orderId: string | null;
  canManage: boolean;
  canOrder?: boolean; // create:order — bakıma bağlı satın alma talebi açabilme
  onClose: () => void;
}

/** Bakım emri detayı: bilgi + durum aksiyonları + yedek parça tüketimi + satın alma + ekler + bağlantılar. */
export function MaintenanceOrderDrawer({ open, orderId, canManage, canOrder = false, onClose }: Props) {
  const qc = useQueryClient();
  const getQ = trpc.maintenance.order.get.useQuery({ id: orderId ?? "" }, { enabled: open && !!orderId, staleTime: 3_000 });
  const setStatusMut = trpc.maintenance.order.setStatus.useMutation();
  const addPartMut = trpc.maintenance.order.addPart.useMutation();
  const removePartMut = trpc.maintenance.order.removePart.useMutation();
  // Yedek parça seçici: stok kartları (Yedek Parça türü öne, sonra diğer stok kartları).
  const partsCatalogQ = trpc.inventory.stockOverview.useQuery(
    { page: 1, pageSize: 100, kinds: ["stock"] },
    { enabled: open && canManage, staleTime: 30_000 },
  );

  const [partSel, setPartSel] = useState<string | null>(null);
  const [partQty, setPartQty] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [completePrompt, setCompletePrompt] = useState(false);
  const [resolveFault, setResolveFault] = useState(true);
  const [downtime, setDowntime] = useState("");
  const [labor, setLabor] = useState("");
  const [notes, setNotes] = useState("");
  const [cancelPrompt, setCancelPrompt] = useState(false);
  const [poReqOpen, setPoReqOpen] = useState(false);

  useEffect(() => { if (open) { setPartSel(null); setPartQty(""); setErr(null); setCompletePrompt(false); setResolveFault(true); setDowntime(""); setLabor(""); setNotes(""); setCancelPrompt(false); setPoReqOpen(false); } }, [open, orderId]);

  const o = getQ.data;
  const partOptions = useMemo(() => {
    const rows = (partsCatalogQ.data?.rows ?? []) as Array<{ id: string; code: string; name: string; uom: string; onHand: number }>;
    return rows.map((r) => ({ value: r.id, label: r.name, desc: r.code, tail: `${fmtQty(r.onHand)} ${r.uom}` }));
  }, [partsCatalogQ.data]);

  const intOrNull = (v: string): number | null => { const t = v.trim(); if (!t) return null; const n = Math.round(Number(t.replace(",", "."))); return Number.isFinite(n) && n >= 0 ? n : null; };

  async function doSetStatus(status: OrderStatus, extra?: { resolveFault?: boolean }) {
    if (!orderId) return;
    setErr(null);
    try {
      await setStatusMut.mutateAsync({
        id: orderId, status,
        downtimeMin: intOrNull(downtime), laborMin: intOrNull(labor),
        resolutionNotes: notes.trim() || null,
        resolveFault: extra?.resolveFault ?? false,
      });
      await qc.invalidateQueries();
      setCompletePrompt(false); setCancelPrompt(false);
    } catch (e) { setErr((e as Error).message); setCompletePrompt(false); setCancelPrompt(false); }
  }

  async function addPart() {
    if (!orderId || !partSel) return;
    const n = Number(partQty.replace(",", "."));
    if (!(n > 0)) { setErr("Geçerli bir parça miktarı girin."); return; }
    setErr(null);
    try {
      await addPartMut.mutateAsync({ orderId, itemId: partSel, qty: n });
      await qc.invalidateQueries();
      setPartSel(null); setPartQty("");
    } catch (e) { setErr((e as Error).message); }
  }

  const active = o && (o.status === "open" || o.status === "in_progress" || o.status === "waiting_parts");
  const meta = o ? STATUS_META[o.status as OrderStatus] : null;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      size="xl"
      title={o ? `${o.code} — ${o.title}` : "Bakım Emri"}
      subtitle={o ? `${TYPE_LABELS[o.type] ?? o.type}${o.location ? ` · ${o.location.name}` : ""}` : undefined}
      footer={<button type="button" className="btn-md ghost" onClick={onClose}>Kapat</button>}
    >
      {err ? <div className="form-banner-err" style={{ marginBottom: 12 }}>{err}</div> : null}
      {getQ.isLoading || !o ? (
        <div style={{ padding: 8, color: "var(--neutral-500)", fontSize: 13 }}>Yükleniyor…</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {/* Durum + aksiyonlar */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            {meta ? <span className={`badge-pill ${meta.cls}`}><Icon name={meta.icon} size={12} />{meta.label}</span> : null}
            {o.priority ? <span className="badge-pill">{SEV_LABELS[o.priority as Severity]}</span> : null}
            {o.fault ? <span className="badge-pill b-danger"><Icon name="alertTriangle" size={12} />Arızadan</span> : null}
            {o.plan ? <span className="badge-pill b-info"><Icon name="refresh" size={12} />Plandan</span> : null}
            <span style={{ flex: 1 }} />
            {canManage && active ? (
              <div className="mo-actions">
                {o.status !== "in_progress" ? (
                  <button type="button" className="btn-md secondary" onClick={() => doSetStatus("in_progress")} disabled={setStatusMut.isPending}><Icon name="play" size={14} />Başlat</button>
                ) : null}
                {o.status !== "waiting_parts" ? (
                  <button type="button" className="btn-md warn" onClick={() => doSetStatus("waiting_parts")} disabled={setStatusMut.isPending}><Icon name="boxes" size={14} />Parça Bekliyor</button>
                ) : null}
                <button type="button" className="btn-md primary" onClick={() => setCompletePrompt(true)} disabled={setStatusMut.isPending}>
                  <Icon name="check" size={14} />Tamamla
                </button>
                <button type="button" className="btn-md danger-outline" onClick={() => setCancelPrompt(true)} disabled={setStatusMut.isPending}>
                  <Icon name="x" size={14} />İptal
                </button>
              </div>
            ) : null}
          </div>

          {/* Bilgi ızgarası */}
          <div className="d-card">
            <div className="d-card-body tight" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10, fontSize: 12.5 }}>
              <div><div style={{ fontSize: 10.5, fontWeight: 600, textTransform: "uppercase", color: "var(--neutral-500)" }}>Ekipman</div>{o.location ? <>{o.location.name} <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--neutral-500)" }}>{o.location.code}</span></> : "—"}</div>
              <div><div style={{ fontSize: 10.5, fontWeight: 600, textTransform: "uppercase", color: "var(--neutral-500)" }}>Atanan</div>{o.assignedTo?.name ?? "—"}</div>
              <div><div style={{ fontSize: 10.5, fontWeight: 600, textTransform: "uppercase", color: "var(--neutral-500)" }}>Bildiren</div>{o.reportedBy ?? "—"}</div>
              <div><div style={{ fontSize: 10.5, fontWeight: 600, textTransform: "uppercase", color: "var(--neutral-500)" }}>Planlanan</div>{fmtDate(o.plannedDate)}</div>
              <div><div style={{ fontSize: 10.5, fontWeight: 600, textTransform: "uppercase", color: "var(--neutral-500)" }}>Başlangıç</div>{fmtDateTime(o.startedAt)}</div>
              <div><div style={{ fontSize: 10.5, fontWeight: 600, textTransform: "uppercase", color: "var(--neutral-500)" }}>Bitiş</div>{fmtDateTime(o.completedAt)}</div>
              {o.downtimeMin != null ? <div><div style={{ fontSize: 10.5, fontWeight: 600, textTransform: "uppercase", color: "var(--neutral-500)" }}>Duruş</div><span style={{ fontFamily: "var(--font-mono)" }}>{o.downtimeMin} dk</span></div> : null}
              {o.laborMin != null ? <div><div style={{ fontSize: 10.5, fontWeight: 600, textTransform: "uppercase", color: "var(--neutral-500)" }}>İşçilik</div><span style={{ fontFamily: "var(--font-mono)" }}>{o.laborMin} dk</span></div> : null}
            </div>
          </div>

          {o.description ? <div style={{ fontSize: 12.5, color: "var(--neutral-700)", whiteSpace: "pre-wrap" }}>{o.description}</div> : null}
          {o.resolutionNotes ? <div style={{ fontSize: 12.5, color: "var(--neutral-700)" }}><b>Sonuç:</b> {o.resolutionNotes}</div> : null}

          {/* Arıza bağlantısı */}
          {o.fault ? (
            <div style={{ border: "1px solid color-mix(in srgb, var(--danger) 25%, transparent)", background: "var(--danger-subtle)", borderRadius: "var(--radius-md)", padding: "8px 12px", fontSize: 12.5 }}>
              <b style={{ color: "var(--danger)" }}>Bağlı Arıza:</b> {o.fault.label}
              {o.fault.severity ? ` · ${SEV_LABELS[o.fault.severity as Severity]}` : ""} · {fmtDateTime(o.fault.startedAt)}
              {" · durum: "}{o.fault.status === "converted" ? "Bakımda" : o.fault.status === "resolved" ? "Çözüldü" : o.fault.status}
            </div>
          ) : null}

          {/* Yedek parçalar */}
          <div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 12.5, fontWeight: 600, color: "var(--neutral-800)" }}>Yedek Parçalar ({o.parts.length})</span>
              {o.partsCost > 0 ? <span style={{ fontSize: 11.5, fontFamily: "var(--font-mono)", color: "var(--neutral-500)" }}>Σ {fmtMoney(o.partsCost)} ₺</span> : null}
            </div>
            {o.parts.length > 0 ? (
              <table className="dt2" style={{ marginBottom: 8 }}>
                <thead><tr><th>Parça</th><th>Lot</th><th className="num">Miktar</th><th className="num">Birim Maliyet</th><th className="num">Tutar</th>{canManage && active ? <th style={{ width: 44 }} /> : null}</tr></thead>
                <tbody>
                  {o.parts.map((p) => (
                    <tr key={p.id}>
                      <td>{p.itemName}<span className="sec mono" style={{ fontSize: 10.5 }}>{p.itemCode}</span></td>
                      <td className="cell-id" style={{ fontSize: 11 }}>{p.lotCode ?? "—"}</td>
                      <td className="cell-num">{fmtQty(p.qty)} <span style={{ fontSize: 10, color: "var(--neutral-400)" }}>{p.uom}</span></td>
                      <td className="cell-num">{p.unitCost != null ? fmtMoney(p.unitCost) : "—"}</td>
                      <td className="cell-num">{p.cost != null ? fmtMoney(p.cost) : "—"}</td>
                      {canManage && active ? (
                        <td>
                          <div className="row-acts">
                            <button type="button" className="iact danger" aria-label="Parçayı iade et" title="İade et (stok geri girer)" onClick={() => removePartMut.mutate({ partId: p.id }, { onSuccess: () => qc.invalidateQueries(), onError: (e) => setErr(e.message) })} disabled={removePartMut.isPending}>
                              <Icon name="x" size={14} />
                            </button>
                          </div>
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div style={{ padding: "10px 12px", border: "1px dashed var(--neutral-300)", borderRadius: "var(--radius-md)", background: "var(--neutral-50)", fontSize: 12, color: "var(--neutral-500)", marginBottom: 8 }}>
                Henüz parça tüketimi yok.
              </div>
            )}
            {canManage && active ? (
              <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
                <div style={{ flex: "1 1 260px", minWidth: 220 }}>
                  <SearchSelect value={partSel} options={partOptions} onChange={setPartSel} placeholder="Yedek parça / stok kartı seç…" searchPlaceholder="Parça ara…" icon="boxes" />
                </div>
                <input className="form-input" style={{ width: 110 }} type="text" inputMode="decimal" value={partQty} onChange={(e) => setPartQty(e.target.value)} placeholder="miktar" aria-label="Parça miktarı" />
                <button type="button" className="btn-md primary" onClick={addPart} disabled={!partSel || addPartMut.isPending}>
                  <Icon name="plus" size={14} />{addPartMut.isPending ? "Düşülüyor…" : "Parça Kullan"}
                </button>
              </div>
            ) : null}
            {canManage && active ? <div style={{ fontSize: 11, color: "var(--neutral-500)", marginTop: 4 }}>Stoktan FEFO ile düşülür; iade edilirse stok geri girer.</div> : null}
          </div>

          {/* Satın Alma Talepleri (bakıma bağlı PO'lar — yedek parça / hizmet) */}
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <span className="mo-sec-title">Satın Alma Talepleri ({o.purchaseOrders.length})</span>
              <span style={{ flex: 1 }} />
              {canOrder && active ? (
                <button type="button" className="btn-md secondary" onClick={() => setPoReqOpen(true)}>
                  <Icon name="cart" size={14} />Satınalma Talebi Oluştur
                </button>
              ) : null}
            </div>
            {o.purchaseOrders.length > 0 ? (
              o.purchaseOrders.map((po) => {
                const sm = PO_STATUS_LABEL[po.status] ?? { label: po.status, cls: "" };
                return (
                  <div className="mo-po-row" key={po.id}>
                    <span className="mo-po-code">{po.code}</span>
                    <span className={`badge-pill ${sm.cls}`}>{sm.label}</span>
                    <span className="mo-po-meta">{po.supplierName ?? "RFQ (tedarikçisiz)"} · {po.lineCount} kalem{po.total != null ? ` · ${fmtMoney(po.total)} ${po.currency === "TRY" ? "₺" : po.currency}` : ""}</span>
                    <span style={{ flex: 1 }} />
                    <Link to="/purchasing" className="btn-md ghost" style={{ textDecoration: "none" }}>Satınalma'da Aç</Link>
                  </div>
                );
              })
            ) : (
              <div style={{ padding: "10px 12px", border: "1px dashed var(--neutral-300)", borderRadius: "var(--radius-md)", background: "var(--neutral-50)", fontSize: 12, color: "var(--neutral-500)" }}>
                Bu emre bağlı satın alma talebi yok.{canOrder && active ? " Yedek parça veya hizmet için talep oluşturabilirsiniz." : ""}
              </div>
            )}
          </div>

          {/* Ekler */}
          <AttachmentPanel maintenanceOrderId={o.id} canEdit={canManage && !!active} />
        </div>
      )}

      {/* Tamamlama onayı: duruş/işçilik/not + arızayı çöz */}
      <ConfirmDialog
        open={completePrompt}
        title="Bakım emrini tamamla?"
        message={
          <div style={{ display: "flex", flexDirection: "column", gap: 10, textAlign: "left" }}>
            <div className="form-row">
              <div className="form-field">
                <label className="lbl" htmlFor="mc-down">Duruş (dk)</label>
                <input id="mc-down" className="form-input" type="text" inputMode="numeric" value={downtime} onChange={(e) => setDowntime(e.target.value)} placeholder="ekipman duruşu" />
              </div>
              <div className="form-field">
                <label className="lbl" htmlFor="mc-labor">İşçilik (dk)</label>
                <input id="mc-labor" className="form-input" type="text" inputMode="numeric" value={labor} onChange={(e) => setLabor(e.target.value)} placeholder="harcanan süre" />
              </div>
            </div>
            <div className="form-field">
              <label className="lbl" htmlFor="mc-notes">Yapılan İş / Sonuç</label>
              <textarea id="mc-notes" className="form-textarea" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} maxLength={2000} />
            </div>
            {o?.fault && o.fault.status !== "resolved" ? (
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, cursor: "pointer" }}>
                <input type="checkbox" checked={resolveFault} onChange={(e) => setResolveFault(e.target.checked)} />
                Bağlı arızayı da çözüldü olarak işaretle
              </label>
            ) : null}
          </div>
        }
        tone="primary"
        confirmLabel="Tamamla"
        cancelLabel="Vazgeç"
        busy={setStatusMut.isPending}
        onConfirm={() => doSetStatus("completed", { resolveFault })}
        onCancel={() => setCompletePrompt(false)}
      />

      <ConfirmDialog
        open={cancelPrompt}
        title="Bakım emrini iptal et?"
        message={
          <>
            <b>{o?.code}</b> iptal edilecek.{o?.fault ? " Bağlı arıza kaydı yeniden 'Açık' duruma döner." : ""}
            {o && o.parts.length > 0 ? " Önce parça satırlarını iade etmelisiniz." : ""}
          </>
        }
        tone="danger"
        confirmLabel="İptal Et"
        cancelLabel="Vazgeç"
        busy={setStatusMut.isPending}
        onConfirm={() => doSetStatus("cancelled")}
        onCancel={() => setCancelPrompt(false)}
      />

      <MaintenancePurchaseRequestDrawer
        open={poReqOpen}
        maintenanceOrderId={o?.id ?? null}
        orderCode={o?.code ?? null}
        orderActive={!!active}
        onClose={() => setPoReqOpen(false)}
        onDone={() => { setPoReqOpen(false); qc.invalidateQueries(); }}
      />
    </Drawer>
  );
}
